import { Fragment, useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../ui/Badge/index.js';
import { Button } from '../../../ui/Button/index.js';
import { GenerateThemeTitlesButton } from '../GenerateThemeTitlesButton/index.js';
import type { GeneratedThemeTitle } from '../../../../hooks/useSnsContentGenerator.js';
import type { ThemeTitleRow } from './useThemeTitleRows.js';

export interface ThemeTitleEditorProps {
  /** 生成対象のアカウントID。URLの `$accountId` をそのまま渡す */
  accountId: string;
  /** 編集中のテーマ・タイトル行一覧 */
  rows: ThemeTitleRow[];
  /** 「テーマ・タイトルを追加」クリック時のハンドラ */
  onAddManualRow: () => void;
  /** 行削除時のハンドラ */
  onRemoveRow: (id: string) => void;
  /** テーマ入力変更時のハンドラ */
  onUpdateTheme: (id: string, theme: string) => void;
  /** タイトル入力変更時のハンドラ */
  onUpdateTitle: (id: string, title: string) => void;
  /** 編集中リストに既にあるテーマ。生成ボタンの重複回避に使う */
  existingThemes: string[];
  /** 生成成功時のハンドラ */
  onGenerated: (items: GeneratedThemeTitle[]) => void;
  /** 生成失敗時のハンドラ */
  onGenerateError: (message: string) => void;
  /** 行ごとの「画像生成対象へ追加」ボタンクリック時のハンドラ */
  onExecuteRow: (id: string) => void;
}

/**
 * テーマ・タイトルの自由記述テキストエリアの共通スタイル。折り返して複数行になってよい代わりに
 * `<input>` ではなく `<textarea>` にしている。文字サイズ・太さは他のInput（size="sm"）と揃える
 * （目立たせるのは列見出し側のスタイルで担う）。
 *
 * @graph-connects none
 */
const themeTitleTextareaClassName = `
  w-full resize-none overflow-hidden rounded-lg border border-[var(--color-border)]
  bg-[var(--color-bg-surface)] px-3 py-1.5
  text-sm text-[var(--color-text-primary)]
  placeholder:text-[var(--color-text-muted)]
  transition-all duration-[var(--transition-fast)]
  hover:border-[var(--color-border-hover)]
  focus:outline-none focus:border-[var(--color-border-focus)] focus:ring-2
  focus:ring-[var(--color-border-focus)] focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)]
`;

/**
 * 内容量に応じて高さが伸縮するtextarea。1行に収まる内容なら1行分の高さ、折り返した分だけ
 * 伸びる。CSSの`field-sizing: content`はSafari系が未対応のため使わず、`scrollHeight`を見て
 * 高さを都度合わせる方式にしている。ユーザーによる手動リサイズ（`resize`）は自動調整と衝突
 * するため許可しない（`themeTitleTextareaClassName`側で`resize-none`にしている）。
 *
 * @graph-connects none
 */
function AutoResizeTextarea({
  value,
  ...props
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'>) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight.toString()}px`;
  }, [value]);

  return <textarea ref={textareaRef} value={value} rows={1} {...props} />;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成画面の編集可能リスト。生成行・手動行を同じ行構造で表示する。行ごとの「画像生成対象へ追加」ボタンで画像生成対象へ個別に追加できる
 * @graph-connects GenerateThemeTitlesButton [calls] 「生成する」ボタンコンポーネント描画
 */
export function ThemeTitleEditor({
  accountId,
  rows,
  onAddManualRow,
  onRemoveRow,
  onUpdateTheme,
  onUpdateTitle,
  existingThemes,
  onGenerated,
  onGenerateError,
  onExecuteRow,
}: ThemeTitleEditorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('snsContentGenerator.noRows')}</p>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_auto_auto] items-center gap-x-2 gap-y-3">
          {/*
            見出し行と各データ行を同じGridコンテナに直接並べる（行ごとにdivで包まない）。
            こうするとauto列（追加ボタン/×列）の幅が全行の内容から共通に決まるため、
            見出しと入力欄の列幅がflexの1fr同士の見た目上のズレなく正確に揃う。
            見出し側は追加ボタン/×の列に対応する空セルを置いて列数を合わせている。
          */}
          <p className="text-sm font-semibold text-[var(--color-text-secondary)]">
            {t('snsContentGenerator.themeColumnLabel')}
          </p>
          <p className="text-sm font-semibold text-[var(--color-text-secondary)]">
            {t('snsContentGenerator.titleColumnLabel')}
          </p>
          <div aria-hidden="true" />
          <div aria-hidden="true" />
          {rows.map((row, index) => (
            <Fragment key={row.id}>
              <AutoResizeTextarea
                value={row.theme}
                onChange={(e) => {
                  onUpdateTheme(row.id, e.target.value);
                }}
                placeholder={t('snsContentGenerator.themePlaceholder')}
                aria-label={t('snsContentGenerator.themeInputLabel', { index: index + 1 })}
                className={themeTitleTextareaClassName}
              />
              <AutoResizeTextarea
                value={row.title}
                onChange={(e) => {
                  onUpdateTitle(row.id, e.target.value);
                }}
                placeholder={t('snsContentGenerator.titlePlaceholder')}
                aria-label={t('snsContentGenerator.titleInputLabel', { index: index + 1 })}
                className={themeTitleTextareaClassName}
              />
              {row.isExecuted ? (
                <Badge variant="success" size="sm">
                  {t('snsContentGenerator.rowExecuted')}
                </Badge>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onExecuteRow(row.id);
                  }}
                  disabled={row.theme.trim() === '' || row.title.trim() === ''}
                >
                  {t('snsContentGenerator.executeRow')}
                </Button>
              )}
              <button
                type="button"
                onClick={() => {
                  onRemoveRow(row.id);
                }}
                aria-label={t('snsContentGenerator.removeRow', { index: index + 1 })}
                className="shrink-0 px-1 text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors"
              >
                ×
              </button>
            </Fragment>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <GenerateThemeTitlesButton
          accountId={accountId}
          existingThemes={existingThemes}
          onGenerated={onGenerated}
          onError={onGenerateError}
        />
        <Button type="button" variant="secondary" size="sm" onClick={onAddManualRow}>
          {t('snsContentGenerator.addManualRow')}
        </Button>
      </div>
    </div>
  );
}
